import { useState } from 'react';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useKeyMetrics } from '../hooks/useWeeklyMetrics';
import { formatCurrency, formatNumber } from '../utils/formatters';
import MetricSparkline from '../components/MetricSparkline';
import type { ComputedMetric, MetricSection, PeriodType } from '../types/weeklyMetrics';
import type { AccountSegment } from '../types/segment';

// ── Period config ───────────────────────────────────────────

const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: 'this-week', label: 'This Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'this-quarter', label: 'This Quarter' },
  { value: 'this-year', label: 'This Year' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'mtd', label: 'MTD vs MTD' },
  { value: 'qtd', label: 'QTD vs QTD' },
  { value: 'ytd', label: 'YTD vs YTD' },
];

const PERIOD_SUBTITLES: Record<PeriodType, string> = {
  'this-week': 'Current week and recent weeks (Sun–Sat)',
  'this-month': 'Current month and recent months',
  'this-quarter': 'Current quarter and recent quarters',
  'this-year': 'Current year and recent years',
  weekly: 'Completed weeks only (Sun–Sat)',
  monthly: 'Completed months only',
  quarterly: 'Completed quarters only',
  annual: 'Completed years only',
  mtd: 'Month-to-date vs same point last month',
  qtd: 'Quarter-to-date vs same point last quarter',
  ytd: 'Year-to-date vs same point last year',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const BASE_PERIOD_MAP: Partial<Record<PeriodType, PeriodType>> = {
  'this-week': 'weekly',
  'this-month': 'monthly',
  'this-quarter': 'quarterly',
  'this-year': 'annual',
  mtd: 'monthly',
  qtd: 'quarterly',
  ytd: 'annual',
};

function formatColumnHeader(key: string, periodType: PeriodType): string {
  const base = BASE_PERIOD_MAP[periodType] ?? periodType;
  switch (base) {
    case 'weekly': {
      const [, m, d] = key.split('-');
      return `${m}/${d}`;
    }
    case 'monthly': {
      const [y, m] = key.split('-');
      return `${MONTH_NAMES[Number(m) - 1]} '${y.slice(2)}`;
    }
    case 'quarterly': {
      const [y, q] = key.split('-');
      return `${q} '${y.slice(2)}`;
    }
    case 'annual':
      return key;
    default:
      return key;
  }
}

const CHANGE_LABELS: Record<PeriodType, string> = {
  'this-week': 'WoW',
  'this-month': 'MoM',
  'this-quarter': 'QoQ',
  'this-year': 'YoY',
  weekly: 'WoW',
  monthly: 'MoM',
  quarterly: 'QoQ',
  annual: 'YoY',
  mtd: 'MTD',
  qtd: 'QTD',
  ytd: 'YTD',
};

const TRAILING_AVG_LABELS: Record<PeriodType, string> = {
  'this-week': '12-wk avg',
  'this-month': '12-mo avg',
  'this-quarter': '8-qtr avg',
  'this-year': '4-yr avg',
  weekly: '12-wk avg',
  monthly: '12-mo avg',
  quarterly: '8-qtr avg',
  annual: '4-yr avg',
  mtd: '12-mo MTD avg',
  qtd: '8-qtr QTD avg',
  ytd: '4-yr YTD avg',
};

// ── Formatting helpers ──────────────────────────────────────

function formatMetricValue(value: number | null, format: string): string {
  if (value == null) return '-';
  if (format === 'currency') return formatCurrency(value);
  if (format === 'decimal') return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return formatNumber(value);
}

// ── Trend helpers ───────────────────────────────────────────

function trendArrow(trend: 'up' | 'down' | 'flat' | null): string {
  if (trend === 'up') return '▲';
  if (trend === 'down') return '▼';
  if (trend === 'flat') return '→';
  return '';
}

function effectiveTrendClasses(et: 'positive' | 'negative' | 'neutral' | null): string {
  if (et === 'positive') return 'bg-green-50 text-green-700';
  if (et === 'negative') return 'bg-red-50 text-red-700';
  if (et === 'neutral') return 'bg-yellow-50 text-yellow-700';
  return '';
}

function changeBadgeClasses(et: 'positive' | 'negative' | 'neutral' | null): string {
  if (et === 'positive') return 'text-green-600';
  if (et === 'negative') return 'text-red-600';
  return 'text-gray-500';
}

// ── Info Tooltip ────────────────────────────────────────────

function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="relative group/tip inline-flex ml-1 align-middle">
      <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 group-hover/tip:text-gray-600 cursor-help" />
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-56 rounded-lg bg-gray-800 px-3 py-2 text-[11px] leading-relaxed text-white shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity z-50 normal-case tracking-normal font-normal">
        {text}
      </span>
    </span>
  );
}

// ── KPI Section Card ────────────────────────────────────────

function SectionCard({ section, periodType }: { section: MetricSection; periodType: PeriodType }) {
  const primary = section.primaryMetric;
  if (!primary) return null;

  const displayValue = formatMetricValue(primary.currentValue, primary.definition.format);
  const changeText =
    primary.changePercent != null
      ? `${primary.changePercent > 0 ? '+' : ''}${primary.changePercent}%`
      : '';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {section.name}
        {primary.definition.description && <InfoTooltip text={primary.definition.description} />}
      </p>
      <p className="text-2xl font-bold text-gray-900">{displayValue}</p>
      <div className="flex items-center gap-2 mt-2">
        <MetricSparkline weeks={primary.periods} effectiveTrend={primary.effectiveTrend} />
        {changeText && (
          <span className={`text-xs font-medium ${changeBadgeClasses(primary.effectiveTrend)}`}>
            {changeText} {trendArrow(primary.trend)}
            <span className="text-gray-400 ml-0.5">{CHANGE_LABELS[periodType]}</span>
          </span>
        )}
      </div>
      {primary.trailingAvg != null && (
        <p className="text-[11px] text-gray-400 mt-1">
          {TRAILING_AVG_LABELS[periodType]}: {formatMetricValue(primary.trailingAvg, primary.definition.format)}
        </p>
      )}
      <p className="text-[11px] text-gray-400 mt-1">{primary.definition.name}</p>
    </div>
  );
}

// ── Metric Row ──────────────────────────────────────────────

function MetricRow({
  metric,
  periodColumns,
}: {
  metric: ComputedMetric;
  periodColumns: string[];
}) {
  const lookup = new Map<string, number>();
  for (const p of metric.periods) {
    lookup.set(p.periodKey, p.value);
  }

  const isTotalRow = metric.definition.name === 'Total';

  return (
    <tr className={`border-b border-gray-100 ${isTotalRow ? 'bg-gray-50' : 'hover:bg-gray-50/50'}`}>
      <td className={`sticky left-0 px-4 py-2 text-sm whitespace-nowrap border-r border-gray-200 z-10 ${isTotalRow ? 'bg-gray-50 font-bold text-gray-900' : 'bg-white font-medium text-gray-800'}`}>
        {metric.definition.name}
        {metric.definition.description && <InfoTooltip text={metric.definition.description} />}
      </td>

      {periodColumns.map((col, idx) => {
        const value = lookup.get(col) ?? null;
        const isCurrent = idx === 0;
        const cellBg = isCurrent ? effectiveTrendClasses(metric.effectiveTrend) : '';

        return (
          <td
            key={col}
            className={`px-4 py-2 text-sm text-right whitespace-nowrap ${cellBg} ${isTotalRow ? 'font-bold' : ''}`}
          >
            {formatMetricValue(value, metric.definition.format)}
            {isCurrent && metric.trend && (
              <span className="ml-1 text-xs">{trendArrow(metric.trend)}</span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ── Main Page Component ─────────────────────────────────────

export default function KeyMetrics() {
  const [periodType, setPeriodType] = useState<PeriodType>('this-week');
  const [selectedSegment, setSelectedSegment] = useState<AccountSegment>('all');
  const { sections, periodColumns, loading, error, refetch } = useKeyMetrics(periodType, selectedSegment);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-eep-navy rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600">{error}</p>
        <button
          onClick={refetch}
          className="mt-4 text-sm text-eep-navy underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Key Metrics</h1>
          <p className="text-sm text-gray-500 mt-1">{PERIOD_SUBTITLES[periodType]}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedSegment}
            onChange={(e) => setSelectedSegment(e.target.value as AccountSegment)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-eep-navy focus:ring-1 focus:ring-eep-navy"
          >
            <option value="all">All Accounts</option>
            <option value="onboarding">Onboarding (0-6 Plans)</option>
            <option value="onboarded">Onboarded (7+ Plans)</option>
          </select>
          <select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as PeriodType)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-eep-navy focus:ring-1 focus:ring-eep-navy"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={refetch}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {sections.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {sections.map((section) => (
            <SectionCard key={section.name} section={section} periodType={periodType} />
          ))}
        </div>
      )}

      {/* Spreadsheet Table */}
      {sections.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="sticky left-0 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 z-10">
                  Metric
                </th>
                {periodColumns.map((col, idx) => (
                  <th
                    key={col}
                    className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide ${
                      idx === 0 ? 'text-eep-navy' : 'text-gray-500'
                    }`}
                  >
                    {formatColumnHeader(col, periodType)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <SectionGroup
                  key={section.name}
                  section={section}
                  periodColumns={periodColumns}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400 text-sm">
          No metrics configured yet. Add SOQL queries to metric definitions to get started.
        </div>
      )}
    </div>
  );
}

// ── Section group inside the table ──────────────────────────

function SectionGroup({
  section,
  periodColumns,
}: {
  section: MetricSection;
  periodColumns: string[];
}) {
  const colSpan = periodColumns.length + 1;
  return (
    <>
      <tr>
        <td
          colSpan={colSpan}
          className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gray-100 border-y border-gray-200"
        >
          {section.name}
          {section.primaryMetric?.definition.description && (
            <InfoTooltip text={section.primaryMetric.definition.description} />
          )}
        </td>
      </tr>
      {section.metrics.map((metric) => (
        <MetricRow key={metric.definition.key} metric={metric} periodColumns={periodColumns} />
      ))}
    </>
  );
}
